import React from 'react';
import { Box, Container, Typography, Paper, Grid, List, ListItem, ListItemText, ListItemIcon } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const MapContainer = styled(Box)(({ theme }) => ({
  height: '400px',
  backgroundColor: '#e5e3df',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  position: 'relative',
  boxShadow: theme.shadows[3],
}));

// Service areas in Northeast Wisconsin
const primaryServiceAreas = [
  'Green Bay',
  'Appleton',
  'De Pere',
  'Neenah',
  'Menasha',
  'Kaukauna',
  'Oshkosh',
];

const doorCountyAreas = [
  'Sturgeon Bay',
  'Fish Creek',
  'Egg Harbor',
  'Sister Bay',
  'Baileys Harbor',
  'Ephraim',
  'Algoma',
];

const secondaryServiceAreas = [
  'Little Chute',
  'Kimberly',
  'Ashwaubenon',
  'Allouez',
  'Bellevue',
  'Howard',
  'Suamico',
  'Hobart',
  'Wrightstown',
  'Pulaski',
];

// Static service area map image
const ServiceAreaImage: React.FC = () => {
  return (
    <Box
      component="img"
      src="/assets/images/service-area.png"
      alt="JBR Delivery service area map showing Northeast Wisconsin including Green Bay, Appleton, Door County, and surrounding areas"
      sx={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
      }}
    />
  );
};

const ServiceAreaMap: React.FC = () => {
  return (
    <Box sx={{ py: 8, backgroundColor: 'grey.50' }}>
      <Container>
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h2"
            component="h2"
            fontWeight={700}
            gutterBottom
          >
            Our Service Areas
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: '800px', mx: 'auto' }}
          >
            JBR Delivery LLC proudly serves Northeast Wisconsin, covering Green Bay, Door County, and surrounding areas.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <MapContainer>
              <ServiceAreaImage />
            </MapContainer>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h5" component="h3" gutterBottom fontWeight={600}>
                Areas We Serve
              </Typography>
              
              <Typography variant="body1" paragraph>
                Our team provides delivery, junk removal, and moving services throughout Northeast Wisconsin, including:
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Primary Service Areas:
                  </Typography>
                  <List dense>
                    {primaryServiceAreas.map((area) => (
                      <ListItem key={area} disableGutters sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={area} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: 'primary.main' }}>
                    Door County:
                  </Typography>
                  <List dense>
                    {doorCountyAreas.map((area) => (
                      <ListItem key={area} disableGutters sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={area} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Additional Areas:
                  </Typography>
                  <List dense>
                    {secondaryServiceAreas.map((area) => (
                      <ListItem key={area} disableGutters sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={area} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Don't see your location? Contact us! We may still be able to serve your area.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ServiceAreaMap;
